import React from 'react'
import ButtonGlobal from '@shared/Button'
import { useFormContext, useFormState } from 'react-hook-form'
import { useState } from 'react'
import { AsanSigning } from '@shared/AsanSigning'
import { ModalProvider } from '@shared/Modal'
import ResumeModalContent from 'pages/Orders/businessCardOrder/entities/ResumeModalContent/ResumeModalContent'
import { useHistory } from 'react-router-dom'
import { Col, Image, Modal, Row, Space, Typography } from 'antd'
import { useFetchBranchListQuery } from 'store/reducers/configs/configs.api'
import {
  AlertIconIntoDetail,
  DownloadBtnIco,
  ModalCloseIco,
  ModalQuestionIco,
} from '@modules/CustomIcons'
import { useTranslation } from 'react-i18next'
import { OrderType } from 'store/types/businessCard'
import CustomResult from '@app/pages/BusinessCards/components/LimitsPage/utils/CustomResult'
import {
  MobilOperatorType,
  OrderBusinessCardType,
  useCardHolderByPinMutation,
  useFetchCreateBusinessCardMutation,
  useUpdateBusinessCardOrderStatusMutation,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import { QueryStatus } from '@reduxjs/toolkit/query'
import { sumAndFormat } from '@shared/libs/sumAndFormat'
import Icon from '@ant-design/icons'
import { FileDownload } from '../../../FileDownload'

export function FieldActionsRight() {
  const [openModal, setOpenModal] = useState(false)
  const { handleSubmit, reset, trigger, getValues } =
    useFormContext<OrderBusinessCardType>()
  const { isValid } = useFormState()

  const navigate = useHistory()
  const { t } = useTranslation()

  const [
    fetchCreateBusinessCard,
    { isLoading, data: orderData, reset: resetQuery, isSuccess },
  ] = useFetchCreateBusinessCardMutation({
    fixedCacheKey: 'createBusinessCard',
  })

  const [_, { reset: resetCardHolder }] = useCardHolderByPinMutation(
    { fixedCacheKey: 'CardHolderByPin' }
  )

  const [
    updateOrderStatus,
    { status: upStatus, isSuccess: isUpSuccess, reset: resetUp },
  ] = useUpdateBusinessCardOrderStatusMutation()

  const { data: branchList = [] } = useFetchBranchListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const getBranchLabel = (value: string) =>
    branchList.find((item) => item.value === value)?.label || ''

  const resumeCompanyInfo_1 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.cardCurrency'),
      value: getValues('cardCurrency'),
    },
    {
      title: t('businessCardOrders.labels.paySytsem'),
      value: getValues('cardSystem'),
    },
    {
      title: t('businessCardOrders.labels.cardName'),
      value: (
        <div style={{ position: 'relative' }}>
          {' '}
          <span style={{ marginRight: 65 }}>{getValues('cardName')}</span>{' '}
          <div
            style={{
              borderRadius: '4px',
              padding: '4px',
              backgroundColor: '#fff',
              border: '1px solid #D9E3EC',
              cursor: 'pointer',
              position: 'absolute',
              top: -9,
              right: 0,
            }}
          >
            <Image
              width={49}
              height={30}
              preview={false}
              src={getValues('cardImage')}
            />
          </div>
        </div>
      ),
    },
    {
      title: t('businessCardOrders.labels.cardDuration'),
      value: `${getValues('cardDuration')} ${t(
        'businessCardOrders.labels.year'
      )}`,
    },
    {
      title: t('businessCardOrders.labels.issueFree'),
      value: `${Number(getValues('commission.issueFee')).toFixed(2)} AZN`,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.note'),
      value: getValues('note'),
    },
  ]

  const resumeCompanyInfo_2 = [
    {
      title: t('businessCardOrders.labels.cardAccount'),
      value: getValues('cardAccount'),
    },
    {
      title: t('businessCardOrders.labels.accountBranch'),
      value: getBranchLabel(getValues('accountBranch')),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.BranchTransferred'),
      value: getBranchLabel(getValues('transferredBranch')),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.receivingTransfer'),
      value: t(
        `orders.salaryCardOrder.constants.${getValues('orderDeliveryType')}`
      ),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.orderTypes'),
      value: t(`orders.salaryCardOrder.constants.${getValues('orderType')}`),
    },
    {
      title: t('businessCardOrders.labels.totalCommission'),
      value: `${Number(getValues('commission.preparationFee')).toFixed(2)} AZN`,
    },
  ]

  const resumeCompanyInfo_3 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.fullName'),
      value: getValues('trustedPerson.fullName'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.pin'),
      value: getValues('trustedPerson.pin'),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.phoneNumber'),
      value: getValues('trustedPerson.phone'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.expiryDate'),
      value: getValues('trustedPerson.expiryDate'),
    },
    {
      title: t('businessCardOrders.labels.totalPay'),
      value: `${sumAndFormat(
        getValues('commission.issueFee'),
        getValues('commission.preparationFee')
      )} AZN`,
    },
  ]
  const resumeUserInfo_1 = [
    {
      title: t('businessCardOrders.labels.cardHolderFullName'),
      value: getValues('cardHolder.cardHolderFullName'),
    },
    {
      title: (
        <>
          {t('businessCardOrders.labels.phoneNumber')} (
          <span style={{ color: '#015CD1' }}>
            {MobilOperatorType[getValues('cardHolder.mobileOperatorName')]}
          </span>
          )
        </>
      ),
      value: getValues('cardHolder.phoneNumber'),
    },
  ]

  const resumeUserInfo_2 = [
    {
      title: t('businessCardOrders.labels.pin'),
      value: getValues('cardHolder.pin'),
    },
    {
      title: t('businessCardOrders.labels.serialTypeAndNo'),
      value:
        getValues('cardHolder.serialType') +
        ' ' +
        getValues('cardHolder.serialNo'),
    },
  ]

  const resumeUserInfo_3 = []

  if (getValues('frontFileId')) {
    resumeUserInfo_3.push({
      title: (
        <FileDownload fileId={getValues('frontFileId')} title={t('businessCardOrders.idcard.frontFileName')} />
      ),
      value: <Icon component={DownloadBtnIco} />,
    })
  }

  if (getValues('backFileId')) {
    resumeUserInfo_3.push({
      title: (
        <FileDownload
          fileId={getValues('backFileId')}
          title={t('businessCardOrders.idcard.backFileName')}
        />
      ),
      value: <Icon component={DownloadBtnIco} />,
    })
  }

  return (
    <>
      <ButtonGlobal
        customType="default"
        color="#015CD1"
        onClick={() => {
          setOpenModal(true)
        }}
      >
        {t('orders.salaryCardOrder.buttonsLabels.update')}
      </ButtonGlobal>
      <ModalProvider
        title={t('orders.detailsOrder')}
        destroyOnClose
        width="calc(100vw - 30%)"
        modalContent={
          <ResumeModalContent
            title={t('businessCardOrders.labels.cardHolderInfoTitle')}
            tableData={[resumeUserInfo_1, resumeUserInfo_2, resumeUserInfo_3]}
            dataSource={[
              resumeCompanyInfo_1,
              resumeCompanyInfo_2,
              resumeCompanyInfo_3,
            ]}
          />
        }
        footer={({ closeModal }) => (
          <Row justify="space-between" align="middle">
            <Col span={6}>
              {getValues('orderType') === OrderType.URGENT && (
                <Row
                  style={{
                    width: '350px',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F6FA',
                    fontSize: '10px',
                    fontWeight: 500,
                    display: 'flex',
                  }}
                >
                  <div style={{ marginRight: '8px' }}>
                    <AlertIconIntoDetail />
                  </div>

                  <Typography.Text>
                    {t('orders.salaryCardOrder.constants.commissionFee')}
                  </Typography.Text>
                </Row>
              )}
            </Col>
            <Col>
              <Space>
                <ButtonGlobal
                  customType="default"
                  color="#015CD1"
                  onClick={() => closeModal()}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.close')}
                </ButtonGlobal>
                <ModalProvider
                  title={t('common.attention')}
                  destroyOnClose
                  afterClose={() => {
                    resetQuery()
                    closeModal()
                    if (isSuccess) {
                      reset()
                      navigate.push('/orders/business-card')
                    }
                  }}
                >
                  {({ openModal, closeModal, setStatus }) => (
                    <ButtonGlobal
                      loading={isLoading}
                      customType="primary"
                      disabled={!isValid}
                      onClick={() => {
                        openModal(QueryStatus.pending)

                        handleSubmit((data) => {
                          fetchCreateBusinessCard(data)
                            .unwrap()
                            .then((res) => {
                              setStatus({
                                status: QueryStatus.fulfilled,
                                message: t('orders.createdSucess'),
                              })
                            })
                            .catch((err) =>
                              setStatus({
                                status: QueryStatus.rejected,
                                message: err.data,
                              })
                            )
                        })()
                      }}
                    >
                      {t('newTransfer.create')}
                    </ButtonGlobal>
                  )}
                </ModalProvider>
                <AsanSigning
                  docType="BCO"
                  asanKey="asanSignWithCreate"
                  handleSigning={() => {}}
                  checkSignCallback={() => isValid || isLoading}
                  status={upStatus}
                  message={
                    isUpSuccess
                      ? t('orders.salaryCardOrder.restrictionTexts.asanSuccess')
                      : undefined
                  }
                  onComplete={(data) => {
                    updateOrderStatus({
                      orderNo: orderData.orderNo,
                      ...data,
                    })
                  }}
                  afterCloseFn={() => {
                    reset()
                    resetQuery()
                    resetUp()
                    closeModal()
                    navigate.push('/orders/business-card')
                  }}
                  beforeFromSigning={async () => {
                    const isValid = await trigger()

                    if (isValid) {
                      let requestData
                      await handleSubmit((data) => {
                        requestData = data
                      })()

                      return {
                        mutation: fetchCreateBusinessCard,
                        requestData,
                        fileNameKey: 'orderNo',
                        successMessage: `${t('orders.createButAsanError')} `,
                      }
                    }
                  }}
                />
              </Space>
            </Col>
          </Row>
        )}
        bodyStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      >
        {({ openModal }) => (
          <ButtonGlobal
            loading={isLoading}
            customType="primary"
            disabled={!isValid}
            onClick={() => {
              openModal('')
            }}
          >
            {t('newTransfer.nextButton')}
          </ButtonGlobal>
        )}
      </ModalProvider>
      <Modal
        title={t('common.attention')}
        open={openModal}
        centered
        width={500}
        footer={null}
        closeIcon={<ModalCloseIco />}
        onCancel={() => setOpenModal(false)}
      >
        <CustomResult
          icon={<ModalQuestionIco />}
          text={t('businessCardOrders.businessFormReset')}
          buttons={[
            {
              label: t('home.no'),
              type: 'default',
              onClick: () => setOpenModal(false),
            },
            {
              label: t('home.yes'),
              type: 'primary',
              onClick: () => {
                reset()
                resetCardHolder()
                trigger()
                setOpenModal(false)
              },
            },
          ]}
        />
      </Modal>
    </>
  )
}
